/**
 * App tabs store
 *
 * Tracks the list of open "collection" tabs in the main app screen.
 *
 * Ordering rules:
 *   - Newly opened tabs are prepended (most-recent-first / leftmost).
 *   - Re-opening an already-open tab does NOT reshuffle its position —
 *     only the title is refreshed if it has changed.
 *   - Users can manually reorder via drag-and-drop using `reorderTab`.
 *
 * Persisted to sessionStorage so tabs survive hard reloads within a browser
 * tab, but not across sessions.
 */
import { useSyncExternalStore } from "react";
import { ConfiguredLogger } from "@/lib/logger";
const logger = new ConfiguredLogger({ caller: "AppTabsStore" });
const STORAGE_KEY = "app.openCollectionTabs";
const MAX_TABS = 20;
let _tabs = loadFromStorage();
const _listeners = new Set();
function loadFromStorage() {
    if (typeof window === "undefined")
        return [];
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return [];
        return parsed
            .filter((t) => typeof t === "object" &&
            t !== null &&
            typeof t.collectionId === "string" &&
            typeof t.title === "string")
            .slice(0, MAX_TABS);
    }
    catch (e) {
        logger.warn("Failed to load app tabs from storage", {
            error: e instanceof Error ? e.message : String(e),
        });
        return [];
    }
}
function saveToStorage(tabs) {
    if (typeof window === "undefined")
        return;
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
    }
    catch (e) {
        logger.warn("Failed to persist app tabs", {
            error: e instanceof Error ? e.message : String(e),
        });
    }
}
function notify() {
    for (const l of _listeners)
        l();
}
function subscribe(listener) {
    _listeners.add(listener);
    return () => _listeners.delete(listener);
}
function getSnapshot() {
    return _tabs;
}
/** Returns the current list of open collection tabs, newest-first. */
export function useAppCollectionTabs() {
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
/**
 * Open a collection as a tab.
 *
 * If already open, preserves its current position and only refreshes the
 * title. If new, prepends to the list so the newest tab sits leftmost.
 */
export function openCollectionTab(tab) {
    const existingIndex = _tabs.findIndex((t) => t.collectionId === tab.collectionId);
    if (existingIndex >= 0) {
        const existing = _tabs[existingIndex];
        if (existing.title === tab.title)
            return;
        _tabs = _tabs.map((t, i) => (i === existingIndex ? tab : t));
        saveToStorage(_tabs);
        notify();
        return;
    }
    _tabs = [tab, ..._tabs].slice(0, MAX_TABS);
    saveToStorage(_tabs);
    notify();
}
/** Close a collection tab. */
export function closeCollectionTab(collectionId) {
    const next = _tabs.filter((t) => t.collectionId !== collectionId);
    if (next.length === _tabs.length)
        return;
    _tabs = next;
    saveToStorage(_tabs);
    notify();
}
/**
 * Swap a tab's collectionId in place — used when promoting a transient
 * smart-collection tab into a real, persisted collection. Preserves the
 * tab's position in the tab strip; updates the title to `nextTitle` if
 * provided.
 *
 * No-op if the source id isn't open.
 */
export function replaceTabId(prevId, nextId, nextTitle) {
    const idx = _tabs.findIndex((t) => t.collectionId === prevId);
    if (idx < 0)
        return;
    const existing = _tabs[idx];
    const next = {
        collectionId: nextId,
        title: nextTitle ?? existing.title,
    };
    _tabs = _tabs.map((t, i) => (i === idx ? next : t));
    saveToStorage(_tabs);
    notify();
}
/**
 * Move the tab at `fromIndex` to `toIndex`. Both indices are positions in the
 * current tab list. No-ops if either is out of range or equal.
 */
export function reorderTab(fromIndex, toIndex) {
    if (fromIndex === toIndex)
        return;
    if (fromIndex < 0 || fromIndex >= _tabs.length)
        return;
    if (toIndex < 0 || toIndex >= _tabs.length)
        return;
    const next = _tabs.slice();
    const [moved] = next.splice(fromIndex, 1);
    if (!moved)
        return;
    next.splice(toIndex, 0, moved);
    _tabs = next;
    saveToStorage(_tabs);
    notify();
}
